import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexIds } from 'features/fde/helpers/useRolodexIds'
import { postFetcher, RolodexEntryQueryBody } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import useSWR, { Cache, SWRConfiguration, useSWRConfig } from 'swr'
import { RolodexLoadedEntryJson } from 'types'

type RolodexEntryWithId = {
  entry: {
    id: string
  }
}

const ENTRY_CACHE_KEY_PREFIX = 'cached-by-id-rolodex-entry'
const BATCH_FETCH_ENTRIES_CACHE_KEY_PREFIX = 'batch-fetch-rolodex-entries'

type RolodexEntriesCachedByIdOptions = {
  cacheScope?: string
  loadOptions?: Omit<RolodexEntryQueryBody, 'ids' | 'limit'>
}

function createEntryCacheKey(id: string, cacheScope?: string) {
  if (cacheScope) return `${ENTRY_CACHE_KEY_PREFIX}-${cacheScope}-${id}`
  return `${ENTRY_CACHE_KEY_PREFIX}-${id}`
}

function partitionByCache<T extends RolodexEntryWithId>(
  cache: Cache,
  ids: readonly string[],
  cacheScope?: string
): { cached: T[]; uncachedIds: string[] } {
  const cached: T[] = []
  const uncachedIds: string[] = []

  for (const id of ids) {
    const cacheKey = createEntryCacheKey(id, cacheScope)
    const cacheEntry = cache.get(cacheKey)
    if (cacheEntry?.data) {
      cached.push(cacheEntry.data as T)
    } else {
      uncachedIds.push(id)
    }
  }

  return { cached, uncachedIds }
}

function mergeEntriesInOrder<T extends RolodexEntryWithId>(
  ids: readonly string[],
  cached: T[],
  fetched: T[]
): T[] {
  const entriesById = new Map<string, T>()

  for (const entry of cached) {
    entriesById.set(entry.entry.id, entry)
  }
  for (const entry of fetched) {
    entriesById.set(entry.entry.id, entry)
  }

  return ids
    .map((id) => entriesById.get(id))
    .filter((entry): entry is T => entry !== undefined)
}

export type UseRolodexEntriesCachedByIdReturn<T> = {
  data: T[] | undefined
  error: Error | null
  isLoading: boolean
  isValidating: boolean
  mutate: () => Promise<void>
}

export function useOnSuccessCacheEntriesById<
  T extends RolodexEntryWithId = RolodexLoadedEntryJson,
>(cacheScope?: string): (data: T[]) => void {
  const { mutate: swrMutate } = useSWRConfig()

  return useCallback(
    (entries: T[]) => {
      for (const entry of entries) {
        const cacheKey = createEntryCacheKey(entry.entry.id, cacheScope)
        swrMutate(cacheKey, entry, { revalidate: false })
      }
    },
    [cacheScope, swrMutate]
  )
}

export function useRolodexEntriesCachedById<
  T extends RolodexEntryWithId = RolodexLoadedEntryJson,
>(
  ids: readonly string[],
  swrConfig?: SWRConfiguration,
  options?: RolodexEntriesCachedByIdOptions
): UseRolodexEntriesCachedByIdReturn<T> {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { cache } = useSWRConfig()
  const cacheEntriesById = useOnSuccessCacheEntriesById<T>(options?.cacheScope)

  const endpoint = `${ROLODEX_URL}/api/v2/entries/load`

  const fetcher = useMemo(() => {
    return async (): Promise<T[]> => {
      if (!ids || ids.length === 0) return []

      const { cached: cachedEntries, uncachedIds } = partitionByCache<T>(
        cache,
        ids,
        options?.cacheScope
      )

      if (uncachedIds.length === 0) {
        return mergeEntriesInOrder(ids, cachedEntries, [])
      }

      const body = JSON.stringify({
        ...options?.loadOptions,
        ids: uncachedIds,
        limit: uncachedIds.length,
      })
      const fetchedEntries: T[] = await postFetcher(endpoint, body, axios)

      cacheEntriesById(fetchedEntries)

      return mergeEntriesInOrder(ids, cachedEntries, fetchedEntries)
    }
  }, [
    ids,
    endpoint,
    axios,
    cache,
    cacheEntriesById,
    options?.cacheScope,
    options?.loadOptions,
  ])

  const batchFetchEntriesSWRKey = useMemo(() => {
    if (!ids || ids.length === 0) return null
    return [
      BATCH_FETCH_ENTRIES_CACHE_KEY_PREFIX,
      options?.cacheScope ?? '',
      [...ids],
      endpoint,
      options?.loadOptions,
    ]
  }, [ids, endpoint, options?.cacheScope, options?.loadOptions])

  const {
    data: entries,
    isLoading,
    isValidating,
    error,
    mutate: mutateEntries,
  } = useSWR<T[]>(batchFetchEntriesSWRKey, fetcher, swrConfig)

  const mutate = useCallback(async () => {
    await mutateEntries()
  }, [mutateEntries])

  return {
    data: entries,
    error: error ?? null,
    isLoading,
    isValidating,
    mutate,
  }
}

export function useClearRolodexEntriesCachedByIdOnScopeChange(
  cacheScope: string | undefined
): void {
  const { cache, mutate: swrMutate } = useSWRConfig()
  const previousCacheScopeRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!cacheScope || previousCacheScopeRef.current === cacheScope) return

    previousCacheScopeRef.current = cacheScope

    for (const key of cache.keys()) {
      if (typeof key !== 'string') continue
      if (
        key.includes(ENTRY_CACHE_KEY_PREFIX) ||
        key.includes(BATCH_FETCH_ENTRIES_CACHE_KEY_PREFIX)
      ) {
        swrMutate(key, undefined, { revalidate: false })
      }
    }
  }, [cache, cacheScope, swrMutate])
}

export function useRolodexEntriesTwoPhaseFetchCachedById<
  T extends RolodexEntryWithId = RolodexLoadedEntryJson,
>(
  loadOptions: RolodexEntryQueryBody,
  swrConfig?: SWRConfiguration
): UseRolodexEntriesCachedByIdReturn<T> {
  const {
    ids,
    isLoading: isLoadingIds,
    isValidating: isValidatingIds,
    error: idsError,
    forceRefresh: refreshIds,
  } = useRolodexIds(loadOptions)

  const {
    data,
    isLoading: isLoadingEntries,
    isValidating: isValidatingEntries,
    error: entriesError,
    mutate: mutateEntries,
  } = useRolodexEntriesCachedById<T>(ids ?? [], swrConfig)

  const mutate = useCallback(async () => {
    await refreshIds()
    await mutateEntries()
  }, [refreshIds, mutateEntries])

  return {
    data,
    error: idsError ?? entriesError ?? null,
    isLoading: isLoadingIds || isLoadingEntries,
    isValidating: isValidatingIds || isValidatingEntries,
    mutate,
  }
}
